export const detectUserTimezone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'UTC';
  } catch {
    return 'UTC';
  }
};

// Basic normalization for a few common aliases; server does full validation
export const normalizeTimezone = (tz?: string | null): string => {
  if (!tz || !tz.trim()) return 'UTC';
  const up = tz.trim().toUpperCase();
  switch (up) {
    case 'GMT':
    case 'GREENWICH':
    case 'ZULU':
      return 'UTC';
    case 'EST':
      return 'America/New_York';
    case 'CST':
      return 'America/Chicago';
    case 'MST':
      return 'America/Denver';
    case 'PST':
      return 'America/Los_Angeles';
    case 'JST':
      return 'Asia/Tokyo';
    case 'PHT':
      return 'Asia/Manila';
    default:
      return tz;
  }
};

export const formatInTimezone = (isoUtc: string, tz: string, options: Intl.DateTimeFormatOptions = {}): string => {
  try {
    const date = new Date(isoUtc);
    const fmt = new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      ...options,
    });
    return fmt.format(date);
  } catch {
    return isoUtc;
  }
};

export const toIsoLocal = (isoUtc: string, tz: string): string => {
  try {
    const date = new Date(isoUtc);
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date);

    const get = (type: string) => parts.find(p => p.type === type)?.value || '00';
    const y = get('year');
    const m = get('month');
    const d = get('day');
    const h = get('hour');
    const min = get('minute');
    const s = get('second');
    return `${y}-${m}-${d}T${h}:${min}:${s}`;
  } catch {
    return isoUtc;
  }
};

// Returns the current user's timezone from persisted auth store, falling back to detection
export const getUserTimezoneFromLocal = (): string => {
  try {
    const raw = localStorage.getItem('auth-store');
    if (!raw) return detectUserTimezone();
    const parsed = JSON.parse(raw);
    const tz = parsed.state?.user?.timeZone as string | undefined;
    return normalizeTimezone(tz || detectUserTimezone());
  } catch {
    return detectUserTimezone();
  }
};

// Returns YYYY-MM-DD for "today" in the provided timezone
export const getTodayDateStringInTimezone = (tz: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const get = (type: string) => parts.find(p => p.type === type)?.value || '00';
    const y = get('year');
    const m = get('month');
    const d = get('day');
    return `${y}-${m}-${d}`;
  } catch {
    return new Date().toISOString().split('T')[0];
  }
};

// Returns JS day index (0=Sun..6=Sat) for a Date as seen in the provided timezone
export const dayOfWeekInTimezone = (date: Date, tz: string): number => {
  try {
    const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: tz }).format(date);
    const map: Record<string, number> = {
      Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
    };
    return map[weekday] ?? date.getDay();
  } catch {
    return date.getDay();
  }
};
